import { differenceInCalendarDays } from 'date-fns'

const TIMEFRAME_LABELS = ['Today', 'Yesterday', 'Last 7 days', 'Last 30 days', 'Older'] as const

export type TimeframeLabel = (typeof TIMEFRAME_LABELS)[number]

export interface TimeframeGroup<T> {
  label: TimeframeLabel
  items: Array<T>
}

function timeframeLabel(daysAgo: number): TimeframeLabel {
  // <= 0 folds future timestamps (clock skew) into Today.
  if (daysAgo <= 0) {
    return 'Today'
  }
  if (daysAgo === 1) {
    return 'Yesterday'
  }
  if (daysAgo <= 7) {
    return 'Last 7 days'
  }
  if (daysAgo <= 30) {
    return 'Last 30 days'
  }
  return 'Older'
}

/**
 * Buckets list items into calendar-day timeframes for the grouped lists every
 * full-list View renders (History, For You, Outbox, Reminders — the designs group
 * them the same way). Groups come back in fixed timeframe order (Today →
 * Older) with empty groups omitted; item order within a group follows the
 * input, so a view's own ordering survives grouping.
 */
export function groupByTimeframe<T>(
  items: Array<T>,
  getDate: (item: T) => Date,
  now: Date = new Date(),
): Array<TimeframeGroup<T>> {
  const buckets = new Map<TimeframeLabel, Array<T>>()
  for (const item of items) {
    const label = timeframeLabel(differenceInCalendarDays(now, getDate(item)))
    const bucket = buckets.get(label)
    if (bucket) {
      bucket.push(item)
    } else {
      buckets.set(label, [item])
    }
  }

  return TIMEFRAME_LABELS.flatMap((label) => {
    const bucket = buckets.get(label)
    return bucket ? [{ label, items: bucket }] : []
  })
}

/**
 * A grouped list flattened for a virtualizer: each label is a row of its own,
 * ahead of the items it covers.
 *
 * `isFirst` lives on the row rather than being re-derived from a virtual index,
 * so "which label sits flush against the header" has one definition. `itemIndex`
 * is the item's position in the ungrouped list — History's selection ranges are
 * expressed in those terms.
 *
 * `label` is a plain string, not a `TimeframeLabel`: Reminders prepends its own
 * "Pinned" group ahead of the day buckets (Figma 7652:23138).
 */
export type TimeframeRow<T> =
  | { kind: 'label'; label: string; isFirst: boolean }
  | { kind: 'item'; item: T; itemIndex: number }

/** Groups items by timeframe and flattens the result into virtualizer rows. */
export function toTimeframeRows<T>(items: Array<T>, getDate: (item: T) => Date, now?: Date): Array<TimeframeRow<T>> {
  let itemIndex = 0
  return groupByTimeframe(items, getDate, now).flatMap(
    (group, groupIndex): Array<TimeframeRow<T>> => [
      { kind: 'label', label: group.label, isFirst: groupIndex === 0 },
      ...group.items.map((item): TimeframeRow<T> => ({ kind: 'item', item, itemIndex: itemIndex++ })),
    ],
  )
}

/** Stable key for a row — labels are unique per list, items key off their own id. */
export function timeframeRowKey<T>(row: TimeframeRow<T>, getItemKey: (item: T) => string): string {
  return row.kind === 'label' ? `label:${row.label}` : getItemKey(row.item)
}
